<script setup>
import { ref, watch, computed, reactive } from 'vue';
import { PROJECT_TYPE } from '@/constant';

const projectId = ref(null);
const projectType = ref(null);

const props = defineProps({
  projects: {
    type: Array,
    required: true,
    default: [],
  },
  accountingCompanies: {
    type: Array,
    required: true,
    default: [],
  },
});

const setInitial = () => {
  projectId.value = null;
  projectType.value = null;
};

const emits = defineEmits(['selectProject']);
watch(
  () => projectId.value,
  async value => {
    const project = props.projects.find(item => item.id === value);
    projectType.value = project?.type ?? null;
    emits('selectProject', value);
  },
);
const projectGroups = computed(() => {
  const personalProjects = props.projects.filter(
    project => project.type === PROJECT_TYPE.NORMAL,
  );
  const companyGroups = props.accountingCompanies.map(company => ({
    company_id: company.id,
    projects: props.projects.filter(
      project =>
        project.type === PROJECT_TYPE.COMPANY && project.company_id === company.id,
    ),
  }));
  const hideCompanyName = props.accountingCompanies.length === 1;
  return [
    { label: 'Personal', projects: personalProjects },
    ...companyGroups
      .filter(group => group.projects.length > 0)
      .map(group => ({
        label: hideCompanyName
          ? 'Company'
          : `Company - ${
              props.accountingCompanies.find(c => c.id === group.company_id)
                ?.company_name
            }`,
        projects: group.projects,
      })),
  ];
});
defineExpose({
  setInitial,
});
</script>

<template>
  <div class="flex items-center justify-center w-full mt-4">
    <div class="w-1/5 text-center">
      <button
        class="w-full p-2 text-white bg-blue-700 rounded"
        @click="$emit('addProject')"
      >
        Create
      </button>
    </div>
    <div class="w-3/5 px-2">
      <select
        class="w-full"
        :disabled="projects.length === 0"
        v-model="projectId"
        :class="{ 'cursor-not-allowed': projects.length === 0 }"
      >
        <template v-if="projects.length === 0">
          <option :value="null">add new project</option>
        </template>
        <template v-else>
          <option :value="null">Select a project</option>
          <optgroup
            v-for="group in projectGroups"
            :key="group.label"
            :label="group.label"
          >
            <option
              v-for="project in group.projects"
              :key="`${project.type}-${project.id}`"
              :value="project.id"
            >
              {{ project.name }}
            </option>
          </optgroup>
        </template>
      </select>
    </div>
    <div class="w-1/5 text-center" v-show="projectId && projectType === 'normal'">
      <button
        @click="$emit('editProject', projectId)"
        class="w-full p-2 text-white bg-green-400 rounded"
      >
        Edit
      </button>
    </div>
  </div>
</template>
